
import { Vue, Component } from 'vue-property-decorator';
import { getMyTeamMatches } from '@/api/match';
import {
  createGuestRecruitment,
  CreateGuestRecruitmentRequest,
} from '@/api/guest';
import { getTeamDetail } from '@/api/team';

@Component
export default class GuestRecruit extends Vue {
  private step = 1

  private isLoadingMatches = false

  private upcomingMatches: any[] = []

  private selectedMatchUid = ''

  private selectedMatch: any = null

  // Step 2
  private genderType = 0

  private ageGroups = 0

  private positionType: 'FIELD' | 'GK' | 'ANY' = 'FIELD'

  private fee = 0

  private guaranteedMinutes = 60

  private maxGuests = 3

  // Step 3
  private additionalInfo = ''

  private teamInfo: any = null

  private genderOptions = [
    { label: '남자', value: 1 },
    { label: '여자', value: 2 },
    { label: '남녀무관', value: 0 },
  ]

  private ageOptions = [
    { label: '10대', value: 10 },
    { label: '20대', value: 20 },
    { label: '30대', value: 30 },
    { label: '40대', value: 40 },
    { label: '50대 이상', value: 50 },
    { label: '나이 무관', value: 0 },
  ]

  private positionOptions = [
    { label: '필드', value: 'FIELD' as const },
    { label: '골키퍼', value: 'GK' as const },
  ]

  private feeOptions = [
    { label: '무료', value: 0 },
    { label: '5,000', value: 5000 },
    { label: '8,000', value: 8000 },
    { label: '10,000', value: 10000 },
  ]

  private guaranteedOptions = [
    { label: '30분', value: 30 },
    { label: '60분', value: 60 },
    { label: '80분', value: 80 },
    { label: '100분', value: 100 },
  ]

  get hasQueryMatch(): boolean {
    return !!(this.$route.query.matchUid);
  }

  get teamUid(): string {
    return (this.$route.query.teamUid as string) || '';
  }

  get maxGuestsOptions(): { label: string, value: number }[] {
    const format = this.selectedMatch?.matchFormat || '';
    const formatMaxMap: Record<string, number> = {
      FOUR_VS_FOUR: 8,
      FIVE_VS_FIVE: 10,
      SIX_VS_SIX: 12,
      SEVEN_VS_SEVEN: 14,
    };
    const max = formatMaxMap[format] || 10;
    const options: { label: string, value: number }[] = [];
    for (let i = 1; i <= max; i += 1) {
      options.push({ label: `${i}명`, value: i });
    }
    return options;
  }

  get sortedMatches(): any[] {
    return [...this.upcomingMatches].sort((a: any, b: any) => {
      if (a.hasGuestRecruitment && !b.hasGuestRecruitment) return -1;
      if (!a.hasGuestRecruitment && b.hasGuestRecruitment) return 1;
      return 0;
    });
  }

  created(): void {
    // 팀 정보 로드 (지역 정보 등)
    this.loadTeamInfo();
    // 매치 생성 후 바로 게스트 모집으로 넘어온 경우
    if (this.$route.query.matchUid) {
      this.checkAndSetQueryMatch();
    } else {
      this.loadUpcomingMatches();
    }
  }

  private async checkAndSetQueryMatch(): Promise<void> {
    const matchUid = this.$route.query.matchUid as string;
    try {
      const matchRes = await getMyTeamMatches(this.teamUid, { page: 0, size: 50 });
      const matches = matchRes.data?.content || matchRes.data || [];
      const match = matches.find((m: any) => m.uid === matchUid);
      if (match && match.hasGuestRecruitment) {
        this.$message.warning('해당 매치는 이미 게스트 모집 중입니다.');
        this.setUpcomingMatches(matches);
        return;
      }
    } catch (error) {
      console.error('Failed to check match:', error);
    }
    this.selectedMatchUid = matchUid;
    this.selectedMatch = {
      uid: matchUid,
      matchDate: this.$route.query.matchDate || '',
      matchTime: this.$route.query.matchTime || '',
      stadiumName: this.$route.query.stadium || '',
      matchFormat: this.$route.query.matchFormat || '',
    };
    this.step = 2;
  }

  private async loadTeamInfo(): Promise<void> {
    if (!this.teamUid) return;
    try {
      const res = await getTeamDetail(this.teamUid);
      this.teamInfo = res.data || null;
    } catch (error) {
      console.error('Failed to load team info:', error);
    }
  }

  private async loadUpcomingMatches(): Promise<void> {
    if (!this.teamUid) {
      this.upcomingMatches = [];
      return;
    }

    this.isLoadingMatches = true;
    try {
      const matchRes = await getMyTeamMatches(this.teamUid, {
        page: 0,
        size: 50,
      });
      const matches = matchRes.data?.content || matchRes.data || [];
      this.setUpcomingMatches(matches);
    } catch (error) {
      console.error('Failed to load matches:', error);
      this.upcomingMatches = [];
    } finally {
      this.isLoadingMatches = false;
    }
  }

  private setUpcomingMatches(matches: any[]): void {
    const now = new Date();
    const sevenDaysLater = new Date();
    sevenDaysLater.setDate(now.getDate() + 7);

    this.upcomingMatches = matches.filter((m: any) => {
      const matchDate = new Date(m.matchDate);
      return matchDate >= now && matchDate <= sevenDaysLater;
    });
  }

  private selectMatch(match: any): void {
    if (match.hasGuestRecruitment) {
      this.$message.info('이미 게스트 모집 중인 매치입니다.');
      return;
    }
    this.selectedMatchUid = match.uid;
    this.selectedMatch = match;
  }

  private goToStep2(): void {
    if (!this.selectedMatchUid && !this.hasQueryMatch) {
      this.$message.warning('매치를 선택해주세요.');
      return;
    }
    this.step = 2;
  }

  private goToStep3(): void {
    this.step = 3;
  }

  private goToMatchCreate(): void {
    this.$router.push({
      path: '/match-create',
      query: { teamUid: this.teamUid },
    });
  }

  private formatLabel(format: string): string {
    const map: Record<string, string> = {
      FOUR_VS_FOUR: '4 vs 4',
      FIVE_VS_FIVE: '5 vs 5',
      SIX_VS_SIX: '6 vs 6',
      SEVEN_VS_SEVEN: '7 vs 7',
    };
    return map[format] || format;
  }

  private formatDate(dateStr: string): string {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const days = ['일', '월', '화', '수', '목', '금', '토'];
    const m = d.getMonth() + 1;
    const day = d.getDate();
    const dow = days[d.getDay()];
    return `${m}월 ${day}일 (${dow})`;
  }

  private async handleSubmit(): Promise<void> {
    try {
      const data: CreateGuestRecruitmentRequest = {
        teamUid: this.teamUid,
        matchUid: this.selectedMatchUid || undefined,
        matchDate: this.selectedMatch?.matchDate || '',
        matchTime: this.selectedMatch?.matchTime || '',
        stadiumName: this.selectedMatch?.stadiumName || '',
        regionSido: this.teamInfo?.regionSido || undefined,
        regionSigungu: this.teamInfo?.regionSigungu || undefined,
        genderType: this.genderType,
        ageGroups: this.ageGroups,
        positionType: this.positionType,
        maxGuests: this.maxGuests,
        fee: this.fee,
        guaranteedMinutes: this.guaranteedMinutes,
        additionalInfo: this.additionalInfo || undefined,
      };

      await createGuestRecruitment(data);
      this.$message.success('게스트 모집이 등록되었습니다!');
      const teamCode = this.teamInfo?.teamCode || this.teamInfo?.uid || this.teamUid;
      this.$router.push({
        name: 'TeamPage',
        params: { teamCode },
        query: { tab: 'match' },
      }).catch(() => { /* ignore NavigationDuplicated */ });
    } catch (error) {
      console.error('Failed to create guest recruitment:', error);
      this.$message.error('게스트 모집 등록에 실패했습니다.');
    }
  }
}
